/**
 *
 * Copyright © 2025-2026 Ping Identity Corporation. All right reserved.
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 *
 **/

import { get } from 'svelte/store';

import { configuredJourneysStore } from '$journey/config.store';

import type { StartParam } from '@forgerock/journey-client/types';

import type { StoreItem } from '$journey/config.store';
import type { StageJourneyObject } from '$journey/journey.interfaces';

/**
 * @function captureLinks - This is a callback for onMount that internally handled links and prevents navigation
 * @param {object} linkWrapper - The object return from `bind:this` attribute on an native element
 * @param {object} currentJourney - The current stage's journey object
 */
export function captureLinks(linkWrapper: HTMLElement, currentJourney: StageJourneyObject) {
  const journeys = get(configuredJourneysStore);
  const stack = get(currentJourney.stack);

  linkWrapper.addEventListener('click', (event: Event) => {
    const target = event.target as HTMLElement;
    const href = target.hasAttribute('href') && target.getAttribute('href');

    const { action, journey } = matchJourneyAndDecideAction(href, journeys, stack);

    if (!action) {
      // If no action is required, return early and allow default behavior
      return;
    }

    // Action is required, so prevent default behavior
    event.preventDefault();

    // Now, push or pop accordingly
    if (action === 'push') {
      currentJourney.push({ journey });
    } else if (action === 'pop') {
      currentJourney.pop();
    }
  });
}

/**
 * Exporting this solely to unit test the logic. It is not intended for external use.
 * @private - compares requested and current journey, then pops or pushes accordingly
 * @param href
 * @param journeys
 * @param stack
 */
export function matchJourneyAndDecideAction(
  href: string | false | null,
  journeys: StoreItem[],
  stack: StartParam[],
):
  | { action: 'push'; journey: string }
  | { action: 'pop'; journey?: undefined }
  | { action: null; journey?: undefined } {
  if (href) {
    /**
     * Does this href match an item configured in the journeys?
     */
    const match = journeys.find((item) => {
      return item.match.find((string) => {
        return href === string;
      });
    });

    if (match) {
      if (!match.journey) {
        return { action: null };
      }

      const previousJourney = stack[stack.length - 2];

      if (!previousJourney || previousJourney.journey !== match.journey) {
        return { action: 'push', journey: match.journey };
      } else {
        return { action: 'pop' };
      }
    } else {
      return { action: null };
    }
  } else {
    return { action: null };
  }
}
